import React, {useCallback} from 'react';

import {ErrorFlatButton} from 'src/components/error-flat-button/error-flat-button';
import {RequiredMark} from 'src/components/required-mark';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl} from 'src/contexts/validation-context';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {AccountTeamMemberSuggest} from 'src/modules/create-account/components/account-team-member-suggest';
import {
    ACCOUNT_TEAM_FULL_MESSAGE,
} from 'src/modules/profile/account/tabs/account-profile/account-managers-section/constant';

import {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {RadioButton} from '@pb/kit-component-radio-button';


import type {AccountTeamMember} from 'src/modules/create-account/typedef';
import type {CreateConferenceData} from 'src/modules/create-conference/useCreateConference';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';

type Props = {
    accountTeamMembers: AccountTeamMember[],
    setAccountTeamMembers: React.Dispatch<React.SetStateAction<CreateConferenceData>>,
    onFieldChange: (field: string) => void,
};

export const AccountTeam = ({accountTeamMembers, setAccountTeamMembers, onFieldChange}: Props) => {
    const {filteredAccountManagerTypes} = useAccountManagerTypes();
    const onSetAccountManager = useCallback((id: number, selectedUser?: AccountManagerSearchResult) => {
        onFieldChange('Conference Team Members: Select Member');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: prev.managerRelations.map((teamMember, i) => ({
                ...teamMember,
                user: i === id ? selectedUser : teamMember.user,
            })),
        }));
    }, [setAccountTeamMembers, onFieldChange]);

    const onSetRole = useCallback((id: number, role: string) => {
        onFieldChange('Conference Team Members: Role');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: prev.managerRelations.map((teamMember, i) => ({
                ...teamMember,
                type: i === id ? role : teamMember.type,
            })),
        }));
    }, [setAccountTeamMembers, onFieldChange]);

    const onSetPrimary = useCallback((id: number) => {
        onFieldChange('Conference Team Members: Primary');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: prev.managerRelations.map((teamMember, i) => ({
                ...teamMember,
                primary: i === id,
                receiveAccountAlerts: i === id || teamMember.receiveAccountAlerts,
            })),
        }));
    }, [setAccountTeamMembers, onFieldChange]);

    const onSetReceiveAlerts = useCallback((
        id: number,
        data: {event: React.SyntheticEvent<HTMLInputElement, Event>},
    ) => {
        onFieldChange('Conference Team Members: Receive Account Alerts');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: prev.managerRelations.map((teamMember, i) => ({
                ...teamMember,
                receiveAccountAlerts: i === id ? data.event.currentTarget.checked : teamMember.receiveAccountAlerts,
            })),
        }));
    }, [onFieldChange, setAccountTeamMembers]);

    const getAllowedTypes = useCallback((selectedType?: string) => {
        const types = accountTeamMembers.map(({type}) => type);
        return filteredAccountManagerTypes.filter(type => !types.includes(type.code) || (type.code === selectedType));
    }, [accountTeamMembers, filteredAccountManagerTypes]);

    const onAddAccountTeamMember = useCallback(() => {
        onFieldChange('Account Team Members: Add Member');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: [
                ...prev.managerRelations,
                {
                    primary: false,
                    type: getAllowedTypes()[0].code,
                    receiveAccountAlerts: true,
                },
            ],
        }));
    }, [setAccountTeamMembers, onFieldChange, getAllowedTypes]);

    const onRemoveAccountTeamMember = useCallback((removeId: number) => {
        onFieldChange('Account Team Members: Remove Member');
        setAccountTeamMembers(prev => ({
            ...prev,
            managerRelations: prev.managerRelations.filter((member, i) => {
                return i !== removeId;
            }),
        }));
    }, [onFieldChange, setAccountTeamMembers]);

    return (
        <FormSection headline="Account Team">
            <Group direction="v" gap="s" alignItems="start">
                <Group gap="xl">
                    <WidthItem width={430}>
                        <Label><RequiredMark label="Member"/></Label>
                    </WidthItem>
                    <WidthItem width={315}>
                        <Label>Role</Label>
                    </WidthItem>
                    <Label>Options</Label>
                </Group>
                {accountTeamMembers.map((manager, id) => (
                    <Group gap="xl" alignItems="start" key={id}>
                        <ValidatedControl name={`account-team-member-select-${id}`}>
                            {invalid => (
                                <AccountTeamMemberSuggest
                                    id={`account-team-member-select-${id}`}
                                    selectedTeamMember={manager.user}
                                    selectedTeamMembers={accountTeamMembers.map(({user}) => user)}
                                    onSelect={user => onSetAccountManager(id, user)}
                                    placeholder="Account team member"
                                    color={invalid ? 'ERROR' : 'DEFAULT'}
                                />
                            )}
                        </ValidatedControl>
                        <WidthItem width={315}>
                            <ComboBox value={manager.type} onChange={role => onSetRole(id, role)}>
                                {getAllowedTypes(manager.type).map(({code, description}) =>
                                    <ComboboxItem key={code} caption={description} value={code}/>,
                                )}
                            </ComboBox>
                        </WidthItem>
                        <Item className="group-item pdy-xs">
                            <Group gap="xl">
                                <RadioButton
                                    value={id}
                                    label="Primary"
                                    checked={manager.primary}
                                    onChange={onSetPrimary}
                                />
                                <CheckBox
                                    disabled={manager.primary}
                                    checked={manager.receiveAccountAlerts}
                                    label="Account Alerts"
                                    onCheckBoxChange={data => onSetReceiveAlerts(id, data)}
                                />
                                {!manager.primary && (
                                    <ErrorFlatButton
                                        icon={Icons.TIMES_CIRCLE}
                                        label="Remove"
                                        onClick={() => onRemoveAccountTeamMember(id)}
                                    />
                                )}
                            </Group>
                        </Item>
                    </Group>
                ))}
                <Group direction="h" alignItems="center">
                    <FlatButton
                        disabled={
                            accountTeamMembers.length === filteredAccountManagerTypes.length ||
                            accountTeamMembers.some(({user}) => !user) ||
                            getAllowedTypes().length === 0
                        }
                        startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                        onClick={onAddAccountTeamMember}>
                        <b>Add Account Team Member</b>
                    </FlatButton>
                    {getAllowedTypes().length === 0 ? <Info type={{popover: ACCOUNT_TEAM_FULL_MESSAGE}}/> : null}
                </Group>
            </Group>
        </FormSection>
    );
};
